import { http } from '@/providers/apis/http';
import { SessionModel, TenantAccountListModel } from '@/models/auth/session';
import { appConfigService } from '@/providers/services/app/app-config';
import { authSerializer } from '@/providers/serializers/auth/auth';


/**
 *
 * This Auth API Provider is responsible for all authentication related API's.
 *
 */
export class AuthAPI {
  private static INSTANCE = new AuthAPI();

  static get instance() {
    return this.INSTANCE;
  }

  private authNamespace: string = 'api/nucleus-auth';

  private mcNamespace: string = 'api/missioncontrol';

  private adminUtilsNamespace = 'api/admin-utils';

  public get tenantUrl() {
    return appConfigService.getTenantUrl();
  }

  public get appId() {
    return appConfigService.getAdminUiAppId();
  }
  public logInWithCredential(
    usernameOrEmail: string,
    password: string,
    accessToken: string,
  ): Promise<SessionModel> {
    let data;
    if (accessToken) {
      data = { anonymous_token: accessToken, grant_type: 'credential', app_id: this.appId };
    } else {
      data = { app_id: this.appId };
    }
    const endpoint = `${this.mcNamespace}/v2/auth/signin`;
    const token = `${usernameOrEmail}:${password}`;
    const headers = http.getBasicHeaders(token);
    return http.post(endpoint, headers, data).then((response) => {
      return authSerializer.sessionModelSerializer(response.data);
    });
  }

  public signInWithToken(token: string): Promise<SessionModel> {
    const endpoint = `${this.mcNamespace}/v1/auth/token`;
    const headers = http.getTokenHeaders(token);
    return http.post(endpoint, headers).then((response) => {
      return authSerializer.sessionModelSerializer(response.data, token);
    }, (er) => {
      return er.response.status;
    });
  }

  public changePassword(params: any) {
    const endpoint = `${this.mcNamespace}/v1/admin/password/update`;
    const headers = http.getTokenHeaders();
    return http.put(endpoint, headers, params).then((response) => {
      return response.data;
    }, (er) => {
      return er.response.status;
    });
  }

  public signOut() {
    const headers = http.getTokenHeaders();
    const endpoint = `${this.authNamespace}/v2/signout`;
    return http.delete(endpoint, headers);
  }

  public async getTenantLoginType(tenant: TenantAccountListModel) {
    const url = `${this.tenantUrl}/${tenant.tenant_short_name}?tenantId=${tenant.tenant_id}&appId=${this.appId}&lt=${tenant.login_type}&v=l`;
    if (url !== undefined) {
      window.location.href = url;
    }
  }

  public async tenantLoginType(nonce: string) {
    const data = { grant_type: 'anonymous' };
    const endpoint = `${this.authNamespace}/v2/signin`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Nonce ${nonce}`,
    };
    return http.post(endpoint, headers, data).then((response: any) => {
      return authSerializer.sessionModelSerializer(response.data);
    });
  }

  public getAccountsLogin(email: string, token: string) {
    const data = { email, appId: this.appId };
    const endpoint = `${this.adminUtilsNamespace}/v2/users/accounts`;
    const headers = http.getTokenHeaders(token);
    return http.post(endpoint, headers, data).then((response: any) => {
      return response.data;
    });
  }

  public getServerToken() {
    const headers = {
      'Content-Type': 'application/json',
    };
    const endpoint = `${this.authNamespace}/v1/users/access/token`;
    return http.get(endpoint, headers).then((response) => {
      return response.data;
    }, (er) => {
      return er.response.status;
    });
  }

  public setImpersonateToken(token: string) {
    const endpoint = `${this.authNamespace}/v1/users/impersonate/access/token`;
    const headers = http.getTokenHeaders();
    return http.post(endpoint, headers).then((response) => {
      return response.data;
    }).catch((error: any) => {
      return { message: 'error' };
    });
  }
}

export const authAPI = AuthAPI.instance;
